import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { Plus, X, Building2, Globe, Phone, MapPin } from 'lucide-react';
import { searchNeedle, matchesSearch } from '../lib/listSearch.mjs';
import { countWord } from '../lib/countWord.mjs';
import { externalHref } from '../lib/externalHref.mjs';

const VERTICALS = ['data-centers', 'logistics', 'grocery-retail', 'automotive', 'education', 'other'];

const WATCHLIST_STAGE_COLOR = {
  watching:      'bg-slate-100 text-slate-600',
  outreach_sent: 'bg-blue-100 text-blue-600',
  engaged:       'bg-indigo-100 text-indigo-600',
  specifying:    'bg-amber-100 text-amber-700',
  won:           'bg-green-100 text-green-700',
  lost:          'bg-gray-100 text-gray-500',
};

const ROLE_LABEL = {
  owner_developer: 'Owner/Dev',
  general_contractor: 'GC',
  subcontractor: 'Sub',
  architect: 'Architect',
  design_firm: 'Design Firm',
  engineer: 'Engineer',
  other: 'Other',
};

// The shared helper sends an expired session to sign-in and puts the HTTP status on the error, as the page's reads do.
function apiPatch(path, body) {
  return api.patch(path, body);
}

export default function AccountsPage() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [q, setQ] = useState('');
  const seq = useRef(0); // Only the latest list request may update page state.

  // Only a good load knows the count; a failed first load shows its error, not "0 companies" or "No accounts yet".
  const load = useCallback(async () => {
    const mine = ++seq.current;
    try {
      const r = await api.get('/crm/accounts');
      if (mine !== seq.current) return;
      setAccounts(r.accounts || []); setError(''); setLoaded(true);
    } catch (e) { if (mine === seq.current) setError(e.message); }
    finally { if (mine === seq.current) setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const needle = searchNeedle(q);
  const filtered = accounts.filter((a) => matchesSearch(needle, [a.name, a.domain]));

  if (loading) return <div className="p-8 text-[var(--text-secondary)]">Loading accounts…</div>;

  return (
    <div className="sm:p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-[var(--text-primary)]">Accounts</h1>
          {loaded && <p className="text-sm text-[var(--text-secondary)]">{accounts.length} {accounts.length === 1 ? 'company' : 'companies'}</p>}
        </div>
        <div className="flex w-full items-center gap-2 sm:w-auto">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…"
            className="min-w-0 flex-1 rounded-lg border border-[var(--border)] bg-white px-3 py-2 text-sm dark:bg-slate-800 sm:flex-none" />
          <button onClick={() => setShowAdd(true)}
            className="flex shrink-0 items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:opacity-90">
            <Plus size={16} /> Add Account
          </button>
        </div>
      </div>
      {error && <div className="mb-3 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

      <div className="overflow-x-auto rounded-xl border border-[var(--border)]">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-[var(--text-secondary)] dark:bg-slate-800">
            <tr>
              <th className="px-4 py-2.5">Company</th>
              <th className="hidden sm:table-cell px-4 py-2.5">Vertical</th>
              <th className="px-4 py-2.5">Location</th>
              <th className="hidden sm:table-cell px-4 py-2.5 text-center">Contacts</th>
              <th className="hidden sm:table-cell px-4 py-2.5 text-center">Open Leads</th>
              <th className="hidden sm:table-cell px-4 py-2.5">Owner</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((a) => (
              <tr key={a.id} onClick={() => setOpenId(a.id)}
                className="cursor-pointer border-t border-[var(--border)] bg-white hover:bg-gray-50 dark:bg-slate-900 dark:hover:bg-slate-800">
                <td className="break-words px-4 py-3 font-medium text-[var(--text-primary)]">{a.name}
                  {a.domain && <span className="ml-2 text-xs text-[var(--text-secondary)]">{a.domain}</span>}
                  <span className="mt-0.5 block text-xs font-normal text-[var(--text-secondary)] sm:hidden">{[a.vertical, `${a.contact_count ?? 0} ${countWord(a.contact_count ?? 0, 'contact')}`, `${a.open_leads ?? 0} ${countWord(a.open_leads ?? 0, 'open lead')}`].filter(Boolean).join(' · ')}</span></td>
                <td className="hidden sm:table-cell px-4 py-3 text-[var(--text-secondary)]">{a.vertical || '—'}</td>
                <td className="px-4 py-3 text-[var(--text-secondary)]">{[a.city, a.state].filter(Boolean).join(', ') || '—'}</td>
                <td className="hidden sm:table-cell px-4 py-3 text-center">{a.contact_count}</td>
                <td className="hidden sm:table-cell px-4 py-3 text-center">{a.open_leads}</td>
                <td className="hidden sm:table-cell px-4 py-3 text-[var(--text-secondary)]">{a.owner_email?.split('@')[0] || '—'}</td>
              </tr>
            ))}
            {!filtered.length && !!accounts.length && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-[var(--text-secondary)]">No accounts match "{q.trim()}".</td></tr>
            )}
            {!accounts.length && (loaded ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-[var(--text-secondary)]">
                No accounts yet — convert a lead from the Leads board, or add one manually.</td></tr>
            ) : null)}
          </tbody>
        </table>
      </div>

      {openId && <AccountDrawer accountId={openId} onClose={() => setOpenId(null)} onChanged={load} />}
      {showAdd && <AccountModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
    </div>
  );
}

function AccountDrawer({ accountId, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const seq = useRef(0);
  const load = useCallback(async () => {
    const mine = ++seq.current;
    const r = await api.get(`/crm/accounts/${accountId}`);
    if (mine === seq.current) setData(r);
  }, [accountId]);
  useEffect(() => { load().catch((e) => setErr(e.message)); }, [load]);
  if (!data) {
    if (!err) return null;
    // Keep the drawer visible so a failed read explains why it could not open.
    return (
      <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={onClose}>
        <div className="flex h-full w-full max-w-md flex-col bg-white shadow-xl dark:bg-slate-900" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-start justify-between border-b border-[var(--border)] p-5">
            <p className="text-sm text-rose-600">{err}</p>
            <button onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-800"><X size={18} /></button>
          </div>
        </div>
      </div>
    );
  }
  const { account, contacts, leads, projects } = data;
  const websiteHref = externalHref(account.website);

  async function update(patch) {
    setErr('');
    try { await apiPatch(`/crm/accounts/${account.id}`, patch); await load(); onChanged(); }
    catch (e) { setErr(e.message); }
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={onClose}>
      <div className="flex h-full w-full max-w-md flex-col overflow-y-auto bg-white shadow-xl dark:bg-slate-900" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between border-b border-[var(--border)] p-5">
          <div>
            <h2 className="text-lg font-semibold text-[var(--text-primary)]">{account.name}</h2>
            <p className="text-sm text-[var(--text-secondary)]">{account.vertical || 'no vertical set'}</p>
          </div>
          <button onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-800"><X size={18} /></button>
        </div>
        {err && <p className="border-b border-[var(--border)] px-5 py-2 text-sm text-rose-600">{err}</p>}

        <div className="space-y-2 border-b border-[var(--border)] p-5 text-sm text-[var(--text-secondary)]">
          {account.website && <p className="flex items-center gap-2"><Globe size={14} />{websiteHref ? <a className="text-primary hover:underline" href={websiteHref} target="_blank" rel="noreferrer">{account.website}</a> : account.website}</p>}
          {account.phone && <p className="flex items-center gap-2"><Phone size={14} />{account.phone}</p>}
          {(account.city || account.state) && <p className="flex items-center gap-2"><MapPin size={14} />{[account.city, account.state].filter(Boolean).join(', ')}</p>}
          <label className="block pt-2 text-xs font-medium">Vertical
            <select value={account.vertical || ''} onChange={(e) => update({ vertical: e.target.value || null })}
              className="mt-1 w-full rounded-lg border border-[var(--border)] bg-white px-2 py-1.5 text-sm dark:bg-slate-800">
              <option value="">—</option>
              {VERTICALS.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </label>
          <label className="block text-xs font-medium">Owner email
            <input defaultValue={account.owner_email || ''} onBlur={(e) => e.target.value !== (account.owner_email || '') && update({ owner_email: e.target.value || null })}
              className="mt-1 w-full rounded-lg border border-[var(--border)] bg-white px-2 py-1.5 text-sm dark:bg-slate-800" />
          </label>
          <label className="block text-xs font-medium">Notes
            <textarea rows={3} defaultValue={account.notes || ''} onBlur={(e) => e.target.value !== (account.notes || '') && update({ notes: e.target.value || null })}
              className="mt-1 w-full rounded-lg border border-[var(--border)] bg-white px-2 py-1.5 text-sm dark:bg-slate-800" />
          </label>
        </div>

        {/* Contacts */}
        <div className="border-b border-[var(--border)] p-5">
          <h3 className="mb-2 text-sm font-semibold text-[var(--text-primary)]">Contacts ({contacts.length})</h3>
          {contacts.map((c) => (
            <div key={c.id} className="mb-2 rounded-lg border border-[var(--border)] p-3 text-sm">
              <p className="font-medium text-[var(--text-primary)]">{c.name} {c.title && <span className="font-normal text-[var(--text-secondary)]">· {c.title}</span>}</p>
              <p className="text-xs text-[var(--text-secondary)]">{[c.email, c.phone].filter(Boolean).join(' · ') || 'No contact info'}</p>
              {c.role && <p className="text-xs text-[var(--text-secondary)]">{c.role}</p>}
            </div>
          ))}
          {!contacts.length && <p className="text-sm text-[var(--text-secondary)]">None yet.</p>}
        </div>

        {/* Projects via junction */}
        {projects && (
          <div className="border-b border-[var(--border)] p-5">
            <h3 className="mb-2 text-sm font-semibold text-[var(--text-primary)]">Projects ({projects.length})</h3>
            {projects.map((p) => (
              <div key={p.junction_id} className="mb-2 rounded-lg border border-[var(--border)] p-3 text-sm">
                <div className="flex items-start gap-2">
                  <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold ${WATCHLIST_STAGE_COLOR[p.watchlist_stage] || 'bg-gray-100 text-gray-500'}`}>
                    {p.watchlist_stage?.replace('_', ' ') || '?'}
                  </span>
                  <div className="min-w-0">
                    <p className="font-medium text-[var(--text-primary)] truncate">{p.project_name || '—'}</p>
                    <p className="text-xs text-[var(--text-secondary)]">
                      {ROLE_LABEL[p.role] || p.role}
                      {(p.city || p.region) && ` · ${[p.city?.split(',')[0], p.region].filter(Boolean).join(', ')}`}
                      {p.contact_name && ` · Key contact: ${p.contact_name}`}
                    </p>
                  </div>
                </div>
              </div>
            ))}
            {!projects.length && <p className="text-sm text-[var(--text-secondary)]">Not linked to any projects yet.</p>}
          </div>
        )}

        {/* Leads */}
        <div className="p-5">
          <h3 className="mb-2 text-sm font-semibold text-[var(--text-primary)]">Leads ({leads.length})</h3>
          {leads.map((l) => (
            <div key={l.id} className="mb-2 rounded-lg border border-[var(--border)] p-3 text-sm">
              <p className="font-medium text-[var(--text-primary)]">{l.name}</p>
              <p className="text-xs text-[var(--text-secondary)]">{l.stage} · {(l.source || '').replace(/_/g, ' ')} · {new Date(l.created_at).toLocaleDateString()}</p>
            </div>
          ))}
          {!leads.length && <p className="text-sm text-[var(--text-secondary)]">No linked leads.</p>}
        </div>
      </div>
    </div>
  );
}

function AccountModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ name: '', domain: '', vertical: '', phone: '', website: '', city: '', state: '' });
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const input = 'mt-1 w-full rounded-lg border border-[var(--border)] bg-white px-3 py-2 text-sm dark:bg-slate-800';

  async function submit(e) {
    e.preventDefault();
    if (!form.name.trim()) { setErr('Company name required'); return; }
    setSaving(true);
    try {
      const body = Object.fromEntries(Object.entries(form).filter(([, v]) => v !== ''));
      await api.post('/crm/accounts', body);
      onSaved();
    } catch (e2) { setErr(e2.message); setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="w-full max-w-md space-y-3 rounded-xl bg-white p-6 shadow-xl dark:bg-slate-900">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-[var(--text-primary)]"><Building2 size={18} /> Add Account</h2>
        {err && <p className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">{err}</p>}
        <label className="block text-xs font-medium text-[var(--text-secondary)]">Company name *<input className={input} value={form.name} onChange={set('name')} /></label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-xs font-medium text-[var(--text-secondary)]">Domain<input className={input} value={form.domain} onChange={set('domain')} placeholder="acme.com" /></label>
          <label className="block text-xs font-medium text-[var(--text-secondary)]">Vertical
            <select className={input} value={form.vertical} onChange={set('vertical')}>
              <option value="">—</option>{VERTICALS.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </label>
          <label className="block text-xs font-medium text-[var(--text-secondary)]">Phone<input className={input} value={form.phone} onChange={set('phone')} /></label>
          <label className="block text-xs font-medium text-[var(--text-secondary)]">Website<input className={input} value={form.website} onChange={set('website')} /></label>
          <label className="block text-xs font-medium text-[var(--text-secondary)]">City<input className={input} value={form.city} onChange={set('city')} /></label>
          <label className="block text-xs font-medium text-[var(--text-secondary)]">State<input className={input} value={form.state} onChange={set('state')} /></label>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="rounded-lg border border-[var(--border)] px-4 py-2 text-sm">Cancel</button>
          <button type="submit" disabled={saving} className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white disabled:opacity-50">{saving ? 'Saving…' : 'Create'}</button>
        </div>
      </form>
    </div>
  );
}
